import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('lalita_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('lalita_admin_token');
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Session ID for saved properties
const getSessionId = () => {
  let sessionId = localStorage.getItem('lalita_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('lalita_session_id', sessionId);
  }
  return sessionId;
};

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const setupAdmin = (email, password, name) => api.post('/auth/setup', { email, password, name });
export const getCurrentAdmin = () => api.get('/auth/me');
export const changeAdminPassword = (currentPassword, newPassword) =>
  api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });

// Properties
export const getProperties = (params) => api.get('/properties', { params });
export const getProperty = (id) => api.get(`/properties/${id}`);
export const createProperty = (data) => api.post('/properties', data);
export const updateProperty = (id, data) => api.put(`/properties/${id}`, data);
export const deleteProperty = (id) => api.delete(`/properties/${id}`);

// Image Upload
export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Inquiries
export const createInquiry = (data) => api.post('/inquiries', data);
export const getInquiries = (params) => api.get('/inquiries', { params });
export const updateInquiryStatus = (id, status) => api.put(`/inquiries/${id}/status?status=${status}`);

// Valuation Requests
export const createValuationRequest = (data) => api.post('/valuations', data);
export const getValuationRequests = (params) => api.get('/valuations', { params });
export const updateValuationStatus = (id, status) => api.put(`/valuations/${id}/status?status=${status}`);

// Areas
export const getAreas = (activeOnly = false) => api.get('/areas', { params: { active_only: activeOnly } });
export const createArea = (data) => api.post('/areas', data);
export const updateArea = (id, data) => api.put(`/areas/${id}`, data);
export const deleteArea = (id) => api.delete(`/areas/${id}`);

// Currency
export const getCurrencyRate = () => api.get('/currency/rate');

// Cloudinary
export const getCloudinarySignature = (resourceType = 'image', folder = 'properties') => 
  api.get('/cloudinary/signature', { params: { resource_type: resourceType, folder } });
export const getCloudinaryStatus = () => api.get('/cloudinary/status');
export const deleteCloudinaryAsset = (publicId, resourceType = 'image') => 
  api.delete('/cloudinary/delete', { params: { public_id: publicId, resource_type: resourceType } });

// Helper function to upload to Cloudinary.
// Accepts an optional pre-fetched signature so a batch of N files can share a
// single /cloudinary/signature round-trip instead of N.
export const uploadToCloudinary = async (file, folder = 'properties', cachedSig = null) => {
  const sig = cachedSig || (await getCloudinarySignature('image', folder)).data;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('api_key', sig.api_key);
  formData.append('timestamp', sig.timestamp);
  formData.append('signature', sig.signature);
  formData.append('folder', sig.folder);

  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
    { method: 'POST', body: formData }
  );

  return response.json();
};

// Testimonials
export const getTestimonials = (activeOnly = true) => api.get('/testimonials', { params: { active_only: activeOnly } });
export const createTestimonial = (data) => api.post('/testimonials', data);
export const updateTestimonial = (id, data) => api.put(`/testimonials/${id}`, data);
export const deleteTestimonial = (id) => api.delete(`/testimonials/${id}`);

// Saved Properties
export const toggleSavedProperty = (propertyId) => {
  const sessionId = getSessionId();
  return api.post(`/saved-properties?property_id=${propertyId}&session_id=${sessionId}`);
};
export const getSavedProperties = () => {
  const sessionId = getSessionId();
  return api.get(`/saved-properties/${sessionId}`);
};

// Property regeneration
export const regenerateDisplayName = (id) => api.post(`/properties/${id}/regenerate-display-name`);
export const regenerateListingCode = (id) => api.post(`/properties/${id}/regenerate-listing-code`);
export const migrateDisplayNames = () => api.post('/admin/properties/migrate-display-names');
export const migrateRentalStatus = () => api.post('/admin/properties/migrate-rental-status');
export const clearShortStayOwnership = () => api.post('/admin/properties/clear-shortstay-ownership');

// Stats
export const getStats = () => api.get('/stats');
export const getRegions = () => api.get('/regions');
export const getRegion = (slug) => api.get(`/regions/${slug}`);
export const getSubArea = (regionSlug, subSlug) => api.get(`/regions/${regionSlug}/sub-areas/${subSlug}`);
export const getSiteImageSlots = () => api.get('/admin/site-images/slots');
export const putSiteImage = (slot, payload) => api.put(`/admin/site-images/${encodeURIComponent(slot)}`, payload);
export const resetSiteImage = (slot) => api.delete(`/admin/site-images/${encodeURIComponent(slot)}`);

// Blog (public)
export const getBlogPosts = (params) => api.get('/blog/posts', { params });
export const getBlogPost = (slug) => api.get(`/blog/posts/${slug}`);
export const getBlogCategories = () => api.get('/blog/categories');
export const getBlogTags = () => api.get('/blog/tags');

// Blog (admin)
export const adminListBlogPosts = (params) => api.get('/admin/blog/posts', { params });
export const adminGetBlogPost = (id) => api.get(`/admin/blog/posts/${id}`);
export const createBlogPost = (data) => api.post('/admin/blog/posts', data);
export const updateBlogPost = (id, data) => api.put(`/admin/blog/posts/${id}`, data);
export const deleteBlogPost = (id) => api.delete(`/admin/blog/posts/${id}`);

// Seed Data
export const seedData = () => api.post('/seed');

export default api;
